/**
 * The browser's ProjectStore: drafts, definitions and chat in IndexedDB.
 *
 * Behaviour is pinned by the shared conformance suite
 * (src/storage/__tests__/projectStoreConformance.ts), the same one the SQLite store's
 * behaviour is described by, so the two clients cannot drift apart on owner scoping,
 * ordering or partial updates.
 */
import { nanoid } from 'nanoid/non-secure';
import type { Project } from '@/storage/ProjectRepository';
import type { ChatMessage } from '@/llm/types';
import type { ProjectStore, ChatStore, ProjectUpdate, NewProject } from '@/storage/projectStorePort';
import { read, write } from './indexedDb';

export { closeDb } from './indexedDb';

interface MessageRow extends ChatMessage {
  project_id: string;
  seq: number;
}

const now = (): string => new Date().toISOString();

/** The smallest step the timestamp format can represent. */
function bumpMs(iso: string): string {
  return new Date(new Date(iso).getTime() + 1).toISOString();
}

/** Structured-clone the definition so a stored draft never aliases a caller's object. */
function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

async function allProjects(): Promise<Project[]> {
  return read<Project[]>('projects', (s) => s.getAll() as IDBRequest<Project[]>);
}

async function own(id: string, ownerId: string): Promise<Project | null> {
  const row = await read<Project | undefined>('projects', (s) => s.get(id) as IDBRequest<Project | undefined>);
  if (!row || row.owner_id !== ownerId) return null;
  return row;
}

export const IndexedDbProjectStore: ProjectStore = {
  async claimUnowned(ownerId: string): Promise<void> {
    const rows = await allProjects();
    const unowned = rows.filter((r) => r.owner_id === null || r.owner_id === undefined);
    if (unowned.length === 0) return;
    await write('projects', (s) => {
      for (const row of unowned) s.put({ ...row, owner_id: ownerId });
    });
  },

  async getAll(ownerId: string): Promise<Project[]> {
    const rows = await allProjects();
    return rows
      .filter((r) => r.owner_id === ownerId)
      .sort((a, b) => (a.updated_at < b.updated_at ? 1 : a.updated_at > b.updated_at ? -1 : 0));
  },

  async getById(id: string, ownerId: string): Promise<Project | null> {
    return own(id, ownerId);
  },

  async create(project: NewProject, ownerId: string): Promise<Project> {
    const timestamp = now();
    const row: Project = {
      id: nanoid(),
      name: project.name,
      description: project.description ?? '',
      definition: clone(project.definition),
      thumbnail: null,
      created_at: timestamp,
      updated_at: timestamp,
      is_published: false,
      version: 1,
      owner_id: ownerId,
    };
    await write('projects', (s) => s.put(row));
    return row;
  },

  async update(id: string, updates: ProjectUpdate, ownerId: string): Promise<void> {
    const row = await own(id, ownerId);
    if (!row) return;
    /*
     * An edit must sort ahead of EVERY other project, not merely ahead of its own previous
     * value.
     *
     * "Most recently updated first" is the contract and Date.now() has millisecond
     * resolution, so writes inside one tick tie and the order of a tie is undefined.
     * Bumping past this row's own timestamp is not enough: create A at T0, create B at T1,
     * then edit A at T1 — A ties with B and can sort second, which is the project the
     * person just edited disappearing down their list. Stepping past the newest timestamp
     * in the store is the only version of this that holds.
     */
    const newest = (await allProjects()).reduce((max, r) => (r.updated_at > max ? r.updated_at : max), '');
    const stamp = now();
    const updated_at = stamp > newest ? stamp : bumpMs(newest);
    const next: Project = { ...row, updated_at };
    // Only the fields actually given — an undefined must not blank a stored value.
    if (updates.name !== undefined) next.name = updates.name;
    if (updates.description !== undefined) next.description = updates.description;
    if (updates.definition !== undefined) next.definition = clone(updates.definition);
    if (updates.thumbnail !== undefined) next.thumbnail = updates.thumbnail;
    if (updates.is_published !== undefined) next.is_published = updates.is_published;
    await write('projects', (s) => s.put(next));
  },

  async updateName(id: string, name: string, ownerId: string): Promise<void> {
    return IndexedDbProjectStore.update(id, { name }, ownerId);
  },

  async delete(id: string, ownerId: string): Promise<void> {
    const row = await own(id, ownerId);
    if (!row) return;
    await write('projects', (s) => s.delete(id));
    await IndexedDbChatStore.deleteByProjectId(id);
  },

  async duplicate(id: string, ownerId: string): Promise<Project> {
    const row = await own(id, ownerId);
    if (!row) throw new Error('That project does not exist.');
    return IndexedDbProjectStore.create(
      { name: `${row.name} copy`, description: row.description, definition: row.definition },
      ownerId,
    );
  },
};

let seq = 0;

export const IndexedDbChatStore: ChatStore = {
  async getByProjectId(projectId: string): Promise<ChatMessage[]> {
    const rows = await read<MessageRow[]>('messages', (s) => s.getAll() as IDBRequest<MessageRow[]>);
    return rows
      .filter((r) => r.project_id === projectId)
      .sort((a, b) => a.seq - b.seq)
      .map(({ project_id: _p, seq: _s, ...message }) => message);
  },

  async addMessage(projectId: string, message: Omit<ChatMessage, 'id'>): Promise<ChatMessage> {
    const stored: ChatMessage = { ...message, id: nanoid() };
    // Two messages written in the same millisecond must still come back in order, so the
    // sequence is explicit rather than inferred from the timestamp.
    seq += 1;
    const row: MessageRow = { ...stored, project_id: projectId, seq };
    await write('messages', (s) => s.put(row));
    return stored;
  },

  async deleteByProjectId(projectId: string): Promise<void> {
    const rows = await read<MessageRow[]>('messages', (s) => s.getAll() as IDBRequest<MessageRow[]>);
    const mine = rows.filter((r) => r.project_id === projectId);
    if (mine.length === 0) return;
    await write('messages', (s) => {
      for (const row of mine) s.delete(row.id);
    });
  },
};
